import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type {
	PublicPluginAPI,
	OptionalPlugin,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	remixEditExistingExperience,
	REMIX_EDIT_EXISTING_MARKS,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { AIPlugin } from '@atlassian/editor-plugin-ai/aiPluginType';
import type {
	CwrRemixTarget,
	MalleableUiPlugin,
} from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import type {
	ConfluenceUpdateDynamicUiOnPageData,
	CwrRemixInvokedFrom,
} from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { remixRegenerateExperience } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender/remix-regenerate-experience';

import type { OnInlineRemixActionedCallback } from '../command-handlers/command-handler';
import {
	getCwrRemixResultInstrumentationId,
	type OnCwrRemixReplacementCallback,
	type OnCwrRemixResultAppliedCallback,
} from '../types/cwr-remix-result';
import { selectLastRemixResult, type RemixResultTarget } from './remix-result-selection';

// Format mirrors `getMauiUrl` in @atlassian/malleable-ui-components/src/ui/editor-remix/utils/message.ts.
// Inlined here to avoid a cross-package dependency for one URL formula.
const buildMauiUrl = (appId: string): string =>
	`${window.location.origin}/remix/ui/${encodeURIComponent(appId)}`;

export class DocumentDynamicUiUpdateCommandProcessor {
	private onInlineRemixActioned: OnInlineRemixActionedCallback | undefined;

	constructor(
		private editorApi: PublicPluginAPI<
			[AnalyticsPlugin, AIPlugin, OptionalPlugin<MalleableUiPlugin>]
		>,
		onInlineRemixActioned?: OnInlineRemixActionedCallback,
		private onCwrRemixResultApplied: OnCwrRemixResultAppliedCallback | undefined = undefined,
		private beginCwrRemixTarget:
			| ((localId: string) => CwrRemixTarget | undefined)
			| undefined = undefined,
		private onCwrRemixReplacementCancelled: OnCwrRemixReplacementCallback | undefined = undefined,
	) {
		this.onInlineRemixActioned = onInlineRemixActioned;
	}

	// Deferred to finish() so the remix-history transaction is dispatched only
	// after endAIExperience(). Dispatching it while the AI session is still open
	// lets a subsequent click/type discard-and-revert the just-applied update.
	private pendingHistoryStackEntryCommand?: EditorCommand;
	private completedExistingInlineUpdateTargetId?: string;
	private completedRemixResult?: RemixResultTarget;
	/** Attempt token captured from the matching selection. */
	private inlineRovoEditAttemptId?: string;
	/** Last bridge target forwarded to the CE claim boundary, including an explicit missing target. */
	private lastInlineRovoEditClaimTarget?: string;
	private hasForwardedInlineRovoEditClaim = false;

	async streamChunk(
		data: ConfluenceUpdateDynamicUiOnPageData,
		isFinished: boolean,
		_traceId?: string,
		singleInstrumentationId?: string,
		isInlineEdit?: boolean,
		chatRemixInstrumentationId?: string,
		cwrRemixInvokedFrom?: CwrRemixInvokedFrom,
	): Promise<void> {
		// Variant-picker updates are inserted FE-side (useUpdateRemix ->
		// useOnComplete); processing the BE push here would double-insert.
		// Rovo-chat updates aren't tracked, so they still flow through.
		if (
			data.previousMauiId &&
			this.editorApi.malleableUi?.actions.isVariantUpdateInFlight(data.previousMauiId) &&
			fg('platform_editor_ai_remix_toolbar')
		) {
			if (isFinished) {
				// The frontend owns variant insertion; the final chunk closes the experience.
				remixRegenerateExperience.success({
					...(chatRemixInstrumentationId && { instrumentationId: chatRemixInstrumentationId }),
					remixType: data.contentType,
					remixSubtype: data.dynamicUiSubtype,
				});
			}
			return;
		}

		this.claimInlineRovoEditAttempt(data.selectionLocalId);
		const attemptId = this.inlineRovoEditAttemptId;
		if ('__isError' in data && (data as { __isError?: boolean }).__isError) {
			this.reportInlineRovoEditAttempt('generation-error', attemptId);
			if (chatRemixInstrumentationId) {
				remixRegenerateExperience.fail({
					instrumentationId: chatRemixInstrumentationId,
					error: 'backend_action_error',
				});
			}
			return;
		}
		if (!isFinished) {
			return;
		}

		// A safety / AUP violation is an expected handoff. It has no editor replacement to await.
		if (data.isSafetyViolation) {
			this.reportInlineRovoEditAttempt('handoff', attemptId);
			remixRegenerateExperience.success({
				...(chatRemixInstrumentationId && {
					instrumentationId: chatRemixInstrumentationId,
					isSafetyViolation: true,
				}),
				remixType: data.contentType,
				remixSubtype: data.dynamicUiSubtype,
			});
			return;
		}

		const cwrEditLocalId =
			cwrRemixInvokedFrom &&
			data.previousMauiId &&
			isExperimentEnabled('platform_editor_cwr_remix_acceptance')
				? data.selectionLocalId
				: undefined;
		// Begin before the replace so the tracker reports the old app as replaced, not deleted.
		const cwrPrevious = cwrEditLocalId ? this.beginCwrRemixTarget?.(cwrEditLocalId) : undefined;
		const cancelCwrReplacement = () => {
			if (cwrPrevious) {
				this.onCwrRemixReplacementCancelled?.({
					previousLocalId: cwrPrevious.localId,
					previousResultId: cwrPrevious.identity.value,
				});
			}
		};

		// Awaiting matters: it keeps the existing AI experience overlay covering
		// the duplicate roundtrip. `endAIExperience()` is only called from
		// `finish()` after this resolves, via PageUpdateCommandHandler.finishAction.
		try {
			const duplicateAndReplaceMauiApp =
				this.editorApi.malleableUi?.actions.duplicateAndReplaceMauiApp;
			if (!duplicateAndReplaceMauiApp && attemptId) {
				cancelCwrReplacement();
				this.reportInlineRovoEditAttempt('insertion-failed', attemptId);
				if (chatRemixInstrumentationId) {
					remixRegenerateExperience.fail({
						instrumentationId: chatRemixInstrumentationId,
						error: 'missing_app_replacement_handler',
					});
				}
				return;
			}
			if (duplicateAndReplaceMauiApp) {
				this.reportInlineRovoEditAttempt('result-ready', attemptId);
				this.reportInlineRovoEditAttempt('direct-insertion-started', attemptId);
			}
			// `duplicateAndReplaceMauiApp` may duplicate the BE-provided MAUI app
			// under the current page (for permissions). When it does, the live
			// node ends up with the duplicated id — NOT `data.id`. Capture that
			// id so the stack entry URL we record matches the live node, which
			// is what the toolbar lookup keys off.
			let insertionResult: 'inserted' | 'target-missing' | 'failed' | undefined;
			const replaceResult = duplicateAndReplaceMauiApp
				? await duplicateAndReplaceMauiApp({
						previousMauiId: data.previousMauiId,
						newMauiId: data.id,
						localId: data.selectionLocalId,
						requireInsertionAcknowledgement: Boolean(attemptId),
						...((chatRemixInstrumentationId || cwrEditLocalId) && {
							onInsertionResult: (result: 'inserted' | 'target-missing' | 'failed') => {
								insertionResult = result;
							},
						}),
					})
				: undefined;
			if (chatRemixInstrumentationId && insertionResult !== 'inserted') {
				if (insertionResult === 'target-missing') {
					remixRegenerateExperience.abort({
						instrumentationId: chatRemixInstrumentationId,
						reason: 'target_no_longer_available',
					});
				} else {
					remixRegenerateExperience.fail({
						instrumentationId: chatRemixInstrumentationId,
						error: duplicateAndReplaceMauiApp
							? 'app_replacement_failed'
							: 'missing_app_replacement_handler',
					});
				}
			}
			if (cwrRemixInvokedFrom && cwrEditLocalId) {
				if (replaceResult && insertionResult === 'inserted') {
					this.onCwrRemixResultApplied?.({
						actionKey: 'dynamic-ui-create',
						invokedFrom: cwrRemixInvokedFrom,
						result: {
							createdContentMetadata: { remixId: replaceResult.insertedMauiId },
							createdContentType: data.contentType ?? 'maui-app',
							...(data.dynamicUiSubtype && { dynamicUiSubtype: data.dynamicUiSubtype }),
							identity: {
								attribute: 'mauiVersion',
								localId: cwrEditLocalId,
								value: replaceResult.insertedMauiId,
							},
							...(cwrPrevious && {
								previousLocalId: cwrPrevious.localId,
								previousResultId: cwrPrevious.identity.value,
							}),
							userAction: 'edit',
						},
						singleInstrumentationId: getCwrRemixResultInstrumentationId(singleInstrumentationId, 0),
					});
				} else {
					cancelCwrReplacement();
				}
			}
			if (!replaceResult && attemptId) {
				this.reportInlineRovoEditAttempt('insertion-failed', attemptId);
				return;
			}
			if (replaceResult && isInlineEdit && data.previousMauiId && data.selectionLocalId) {
				this.onInlineRemixActioned?.({
					actionKey: 'dynamic-ui-create',
					createdContentMetadata: {
						previousMauiId: data.previousMauiId,
						remixId: replaceResult.insertedMauiId,
					},
					createdContentType: data.contentType ?? 'dynamic-ui',
					dynamicUiSubtype: data.dynamicUiSubtype,
					identity: {
						attribute: 'mauiVersion',
						localId: data.selectionLocalId,
						value: replaceResult.insertedMauiId,
					},
					singleInstrumentationId,
				});
			}
			if (replaceResult && data.previousMauiId && data.selectionLocalId) {
				this.completedExistingInlineUpdateTargetId = data.selectionLocalId;
			}
			if (replaceResult && fg('aifc_edit_maui')) {
				this.completedRemixResult = {
					type: 'extension',
					id: buildMauiUrl(replaceResult.insertedMauiId),
					localId: data.selectionLocalId,
				};
			}
			remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.NODE_INSERTED);
			remixEditExistingExperience.success();
			this.reportInlineRovoEditAttempt('inserted', attemptId);
			if (!chatRemixInstrumentationId || insertionResult === 'inserted') {
				remixRegenerateExperience.success({
					...(chatRemixInstrumentationId && { instrumentationId: chatRemixInstrumentationId }),
					remixType: data.contentType,
					remixSubtype: data.dynamicUiSubtype,
				});
			}

			if (data.previousMauiId && fg('platform_editor_ai_remix_toolbar')) {
				// Fall back to data.id if the action didn't run (e.g. malleableUi
				// plugin unavailable) — better to record an entry keyed off the
				// BE id than to skip recording entirely.
				const insertedMauiId = replaceResult?.insertedMauiId ?? data.id;
				// Forward the backend-confirmed subtype so a type-changing edit
				// ("change to a bar chart", "make it a tree map") updates the style
				// picker. The reducer falls back to inheriting the previous subtype
				// when the backend omits one.
				const backendSubtype = data.dynamicUiSubtype;
				// Stash the command; it is dispatched in finish() after the AI
				// experience ends (see field comment).
				this.pendingHistoryStackEntryCommand =
					this.editorApi.malleableUi?.commands.addBackendRemixHistoryStackEntry({
						previousMauiId: data.previousMauiId,
						url: buildMauiUrl(insertedMauiId),
						dynamicUiType: data.contentType,
						dynamicUiSubtype: backendSubtype,
					});
			}
		} catch (error) {
			cancelCwrReplacement();
			this.reportInlineRovoEditAttempt('insertion-failed', attemptId);
			remixEditExistingExperience.failure({
				metadata: { reason: 'duplicate_replace_failed' },
			});
			remixRegenerateExperience.fail({
				...(chatRemixInstrumentationId && { instrumentationId: chatRemixInstrumentationId }),
				error: error instanceof Error ? error.message : 'duplicate_replace_failed',
				remixType: data.contentType,
				remixSubtype: data.dynamicUiSubtype,
			});
			throw error;
		}
	}

	private claimInlineRovoEditAttempt(targetId: string | undefined): void {
		if (this.hasForwardedInlineRovoEditClaim && this.lastInlineRovoEditClaimTarget === targetId) {
			return;
		}
		this.hasForwardedInlineRovoEditClaim = true;
		this.lastInlineRovoEditClaimTarget = targetId;
		const attemptId = this.editorApi.malleableUi?.actions.claimInlineRovoEdit?.(targetId);
		if (attemptId && !this.inlineRovoEditAttemptId) {
			this.inlineRovoEditAttemptId = attemptId;
		}
	}

	private reportInlineRovoEditAttempt(
		result:
			| 'generation-aborted'
			| 'handoff'
			| 'insertion-failed'
			| 'inserted'
			| 'generation-error'
			| 'result-ready'
			| 'direct-insertion-started',
		attemptId = this.inlineRovoEditAttemptId,
	): void {
		if (!attemptId) {
			if (
				result === 'generation-aborted' ||
				result === 'handoff' ||
				result === 'insertion-failed' ||
				result === 'generation-error'
			) {
				this.hasForwardedInlineRovoEditClaim = false;
				this.lastInlineRovoEditClaimTarget = undefined;
			}
			return;
		}
		this.editorApi.malleableUi?.actions.reportInlineRovoEdit?.(attemptId, result);
		if (
			result === 'generation-aborted' ||
			result === 'handoff' ||
			result === 'insertion-failed' ||
			result === 'inserted' ||
			result === 'generation-error'
		) {
			if (this.inlineRovoEditAttemptId === attemptId) {
				this.inlineRovoEditAttemptId = undefined;
			}
			this.hasForwardedInlineRovoEditClaim = false;
			this.lastInlineRovoEditClaimTarget = undefined;
		}
	}

	abort(): void {
		this.completedRemixResult = undefined;
		this.reportInlineRovoEditAttempt('generation-aborted');
	}

	streamError(): void {
		this.completedRemixResult = undefined;
		this.reportInlineRovoEditAttempt('generation-error');
	}

	finish(): void {
		const aiDecorationState =
			this.editorApi.aiExperience?.sharedState?.currentState()?.aiDecorationExperiencePluginState;
		const completedInlineUpdateTargetId = this.completedExistingInlineUpdateTargetId;
		const isReadyBarCandidate =
			Boolean(completedInlineUpdateTargetId) &&
			aiDecorationState?.inlineRovoStreamingTargetId === completedInlineUpdateTargetId &&
			!aiDecorationState?.isInlineEditReady;
		const isInlineEditTargetInView = isReadyBarCandidate
			? this.editorApi.aiExperience?.actions.isInlineEditTargetInView?.(
					completedInlineUpdateTargetId,
				)
			: undefined;
		if (isReadyBarCandidate && isInlineEditTargetInView !== true) {
			this.editorApi.aiExperience?.actions.endInlineRovoStreaming?.();
			this.editorApi.aiExperience?.actions.showInlineEditReady?.(completedInlineUpdateTargetId);
		}
		this.completedExistingInlineUpdateTargetId = undefined;
		this.editorApi.aiExperience?.actions.endAIExperience();
		this.hasForwardedInlineRovoEditClaim = false;
		this.lastInlineRovoEditClaimTarget = undefined;
		// Record the remix-history stack entry only after the AI experience has
		// ended, so the transaction does not land in the still-open session where a
		// click/type would discard-and-revert the update.
		if (this.pendingHistoryStackEntryCommand) {
			this.editorApi.core?.actions.execute(this.pendingHistoryStackEntryCommand);
			this.pendingHistoryStackEntryCommand = undefined;
		}
		if (fg('aifc_edit_maui') && this.completedRemixResult) {
			this.editorApi.core.actions.execute(selectLastRemixResult([this.completedRemixResult]));
		}
		this.completedRemixResult = undefined;
	}
}
